// React imports
import React, { useEffect, useState, useRef } from 'react';

// Generic imports
import { v4 as uuidv4 } from 'uuid';
import { useDrop } from 'react-dnd'
import { dropPoint } from 'prosemirror-transform'
import _ from 'lodash';
import pluralize from 'pluralize';
import dayjs from 'dayjs';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangle, faDollarSign, faFileCheck, faSigma, faBold, faItalic, faStrikethrough, faList, faListOl, faHeading, faQuoteLeft, faHorizontalRule, faUndo, faRedo, faPaste as faPasteSolid, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { faCopy, faPaste, faScissors, faCode, faHeading as faHeadingLight, faLink, faSquareDollar, faSquarePlus } from '@fortawesome/pro-light-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons'; 

// Material UI imports
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { yellow, green, grey } from '@mui/material/colors';

// Tiptap imports
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Node, mergeAttributes, findChildren, Extension } from '@tiptap/core';
import Paragraph from '@tiptap/extension-paragraph';
import { TextStyle } from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import { richPasteExtensions, createImagePasteHandler, resolveStyleBlocks } from '../../utils/richPaste.js';
import { Fragment, Slice } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'

// Local imports
import currencyToText from '../../../CurrencyToText.js';
import ExcelPasteDialog from './OpportunityOneOffFreeTextBuilder.excelpaste.js';
import { getAcceptQuoteLinkText } from '../OpportunityOneOffEmailBuilder.utils.js';

// Reading the clipboard programmatically (the Paste / Paste special toolbar buttons) is gated
// behind the browser's clipboard-read permission. Chrome silently denies it when the permission
// was dismissed/blocked or the document isn't focused, and Safari/Firefox don't support readText
// from a button at all — in every case it rejects with NotAllowedError. Swallow that instead of
// throwing an uncaught rejection (which the error tracker logs), and tell the user to use the
// keyboard shortcut, which routes through the editor's native paste handler and needs no permission.
async function readClipboardText() {
    try {
        if (!navigator.clipboard || !navigator.clipboard.readText) throw new Error('unsupported');
        return await navigator.clipboard.readText();
    } catch (e) {
        window.alert('Your browser blocked reading the clipboard. Press Ctrl/Cmd+V to paste instead.');
        return null;
    }
}

const DivParagraph = Paragraph.extend({
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});

// Link is provided by StarterKit as a Mark, not a Node
// Removed custom Link Node to avoid conflict with StarterKit's Link Mark

const Field = Node.create({
    name: 'field',
    group: 'inline',
    priority: 1000,
    inline: true,
    atom: true,
    selectable: false,

    addAttributes() {
        return {
            id: { default: '' },
            label: { default: 'Selling Rate' },
            'editor-id': { default: '' },
            value: { default: '' },
            'data-margin': { default: '' },
            'data-profit': { default: '' },
            'data-cost': { default: '' },
            'data-suppliers': { default: '' },
            'data-title': { default: '' },
            'data-section': { default: '' },
            'data-stale': { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-field]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-field': 'true',
                'editor-id': HTMLAttributes['editor-id'],
                'data-margin': HTMLAttributes['data-margin'],
                'data-profit': HTMLAttributes['data-profit'],
                'data-cost': HTMLAttributes['data-cost'],
                'data-suppliers': HTMLAttributes['data-suppliers'],
                'data-title': HTMLAttributes['data-title'],
                'data-section': HTMLAttributes['data-section'],
            }),
            HTMLAttributes.value || HTMLAttributes.label,
        ];
    },

    addNodeView() {
        return ({ node, getPos, editor }) => {
            const NumberFormat = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });

            const outer = document.createElement('span');

            const span = document.createElement('span');
            const valueText = node.attrs.value || node.attrs.label;
            // Currency value
            const valueSpan = document.createElement('span');
            valueSpan.textContent = valueText;
            valueSpan.style.fontWeight = '500';
            valueSpan.style.color = '#334155';
            valueSpan.style.fontSize = '12px';
            span.appendChild(valueSpan);
            span.style.cursor = 'pointer';
            span.style.border = '1px solid #dedede';
            span.style.borderRadius = '6px';
            span.style.padding = '0px 6px';
            span.style.backgroundColor = '#eee';
            span.style.position = 'relative';
            span.style.display = 'inline-flex';
            span.style.flexWrap = 'wrap';
            span.style.alignItems = 'center';
            span.style.gap = '8px';
            span.style.lineHeight = '1.5';

            // Stale warning indicator
            const staleIcon = document.createElement('span');
            staleIcon.textContent = '⚠ click to update';
            staleIcon.style.fontSize = '10px';
            staleIcon.style.color = '#d97706';
            staleIcon.style.fontWeight = '500';
            staleIcon.style.marginLeft = '4px';
            staleIcon.style.display = node.attrs['data-stale'] === 'true' ? 'inline' : 'none';
            span.appendChild(staleIcon);

            if (node.attrs['data-stale'] === 'true') {
                span.style.border = '1px solid #fbbf24';
                span.style.backgroundColor = '#fffbeb';
            }

            outer.appendChild(span);
            outer.appendChild(document.createTextNode(' '));

            const editorId = node.attrs['editor-id'];

            // Function to update badge text
            const updateBadge = (badgeNode, attrs) => {
                const isPositive = (attrs['data-margin'] || 0) >= 0;
                const fieldTitle = attrs['data-title'];
                const sectionTitle = attrs['data-section'];
                const marginStr = 'Margin: ' + NumberFormat.format(attrs['data-margin'] || 0);
                
                // Build the badge text with dot dividers
                const parts = [];
                
                // Add field/section title if available
                if (fieldTitle && fieldTitle !== 'Unnamed Field') {
                    let titleText = fieldTitle;
                    if (sectionTitle) {
                        titleText += ' (' + sectionTitle + ')';
                    }
                    parts.push(titleText);
                } else if (sectionTitle) {
                    parts.push('(' + sectionTitle + ')');
                }
                
                // Add margin if available
                if (attrs['data-margin'] || attrs['data-margin'] === 0) {
                    parts.push(marginStr + '%');
                }
                
                // Add profit/loss if available
                if (attrs['data-profit']) {
                    const profitStr = (isPositive ? 'Profit: ' : 'Loss: ') + attrs['data-profit'];
                    parts.push(profitStr);
                }
                
                // Add costs and suppliers if available
                if (attrs['data-cost']) {
                    let costText = 'Costs: ' + attrs['data-cost'];
                    if (attrs['data-suppliers']) {
                        costText += ' to ' + attrs['data-suppliers'];
                    }
                    parts.push(costText);
                }
                
                badgeNode.textContent = parts.join(' · ');
                badgeNode.style.color = isPositive ? '#1B5E20' : '#c62828';
                badgeNode.style.backgroundColor = isPositive ? '#C8E6C9' : '#ffebee';
                badgeNode.style.border = isPositive ? '1px solid #81C784' : '1px solid #ef5350';
            };

            let combinedBadge = null;
            if ((node.attrs['data-margin'] || node.attrs['data-margin'] === 0) || node.attrs['data-cost']) {
                combinedBadge = document.createElement('span');
                const isPositive = (node.attrs['data-margin'] || 0) >= 0;
                
                updateBadge(combinedBadge, node.attrs);
                
                combinedBadge.style.padding = '2px 10px';
                combinedBadge.style.borderRadius = '12px';
                combinedBadge.style.fontSize = '11px';
                combinedBadge.style.fontWeight = '400';
                combinedBadge.style.display = 'inline-flex';
                combinedBadge.style.alignItems = 'center';
                combinedBadge.style.whiteSpace = 'nowrap';
                combinedBadge.style.position = 'relative';
                combinedBadge.style.cursor = 'pointer';
                combinedBadge.style.transition = 'all 0.2s ease-in-out';
                
                // Add trash icon that appears on hover
                const trashIcon = document.createElement('span');
                trashIcon.innerHTML = '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash" style="width: 10px; height: 10px; margin-left: 6px; opacity: 0; transition: opacity 0.2s; cursor: pointer;" class="svg-inline--fa fa-trash" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M177.7 32l92.5 0c5.5 0 10.6 2.8 13.6 7.5L299.1 64 148.9 64l15.3-24.5c2.9-4.7 8.1-7.5 13.6-7.5zM336.9 64L311 22.6C302.2 8.5 286.8 0 270.3 0L177.7 0C161.2 0 145.8 8.5 137 22.6L111.1 64 64.1 64 32 64 16 64C7.2 64 0 71.2 0 80s7.2 16 16 16l18.3 0L59.8 452.6C62.1 486.1 90 512 123.6 512l200.8 0c33.6 0 61.4-25.9 63.8-59.4L413.7 96 432 96c8.8 0 16-7.2 16-16s-7.2-16-16-16l-16 0-32.1 0-47.1 0zm44.8 32L356.3 450.3C355.1 467 341.2 480 324.4 480l-200.8 0c-16.8 0-30.7-13-31.9-29.7L66.4 96l315.3 0z"></path></svg>';
                trashIcon.style.display = 'inline-flex';
                trashIcon.style.alignItems = 'center';
                trashIcon.style.color = isPositive ? '#1B5E20' : '#c62828';
                
                // Show trash icon on hover and add clickable effect
                combinedBadge.addEventListener('mouseenter', () => {
                    trashIcon.querySelector('svg').style.opacity = '1';
                    combinedBadge.style.transform = 'translateY(-1px)';
                    combinedBadge.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    combinedBadge.style.opacity = '0.9';
                });
                combinedBadge.addEventListener('mouseleave', () => {
                    trashIcon.querySelector('svg').style.opacity = '0';
                    combinedBadge.style.transform = 'translateY(0)';
                    combinedBadge.style.boxShadow = 'none';
                    combinedBadge.style.opacity = '1';
                });
                
                // Delete field on trash icon click
                trashIcon.addEventListener('click', (e) => {
                    e.stopPropagation();
                    editor.chain().focus().command(({ tr }) => {
                        const pos = getPos();
                        if (pos !== null && pos !== undefined) {
                            tr.delete(pos, pos + node.nodeSize);
                            return true;
                        }
                        return false;
                    }).run();
                });

                combinedBadge.appendChild(trashIcon);
                span.appendChild(combinedBadge);
            }

            outer.onclick = () => {
                const event = new CustomEvent(`fieldClicked-${editorId}`, {
                    detail: {
                        id: node.attrs.id,
                        update: (value, margin, profit, cost, suppliers, title, section) => {
                            editor.chain().focus().command(({ tr }) => {
                                const pos = getPos();
                                if (pos === undefined || pos === null) {
                                    return false;
                                }
                                tr.setNodeMarkup(pos, undefined, {
                                    ...node.attrs,
                                    value,
                                    'data-margin': margin || '',
                                    'data-profit': profit || '',
                                    'data-cost': cost || '',
                                    'data-suppliers': suppliers || '',
                                    'data-title': title || '',
                                    'data-section': section || '',
                                });
                                return true;
                            }).run();
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent(`fieldUpdated-${editorId}`));
                            }, 100);
                        },
                    },
                });

                window.dispatchEvent(new CustomEvent(`fieldClicked-${editorId}`, event));
            };

            return { 
                dom: outer,
                update: (newNode) => {
                    // Update value span
                    const newValueText = newNode.attrs.value || newNode.attrs.label;
                    if (valueSpan.textContent !== newValueText) {
                        valueSpan.textContent = newValueText;
                    }

                    // Update stale indicator
                    const isStale = newNode.attrs['data-stale'] === 'true';
                    staleIcon.style.display = isStale ? 'inline' : 'none';
                    span.style.border = isStale ? '1px solid #fbbf24' : '1px solid #dedede';
                    span.style.backgroundColor = isStale ? '#fffbeb' : '#eee';

                    // Update badge if it exists
                    if (combinedBadge) {
                        updateBadge(combinedBadge, newNode.attrs);
                    } else if ((newNode.attrs['data-margin'] || newNode.attrs['data-margin'] === 0) || newNode.attrs['data-cost']) {
                        // Badge didn't exist before but should now - create it
                        combinedBadge = document.createElement('span');
                        const isPositive = (newNode.attrs['data-margin'] || 0) >= 0;
                        
                        updateBadge(combinedBadge, newNode.attrs);
                        
                        combinedBadge.style.padding = '2px 10px';
                        combinedBadge.style.borderRadius = '12px';
                        combinedBadge.style.fontSize = '11px';
                        combinedBadge.style.fontWeight = '400';
                        combinedBadge.style.display = 'inline-flex';
                        combinedBadge.style.alignItems = 'center';
                        combinedBadge.style.whiteSpace = 'nowrap';
                        combinedBadge.style.position = 'relative';
                        combinedBadge.style.cursor = 'pointer';
                        combinedBadge.style.transition = 'all 0.2s ease-in-out';
                        
                        // Add trash icon
                        const trashIcon = document.createElement('span');
                        trashIcon.innerHTML = '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash" style="width: 10px; height: 10px; margin-left: 6px; opacity: 0; transition: opacity 0.2s; cursor: pointer;" class="svg-inline--fa fa-trash" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M177.7 32l92.5 0c5.5 0 10.6 2.8 13.6 7.5L299.1 64 148.9 64l15.3-24.5c2.9-4.7 8.1-7.5 13.6-7.5zM336.9 64L311 22.6C302.2 8.5 286.8 0 270.3 0L177.7 0C161.2 0 145.8 8.5 137 22.6L111.1 64 64.1 64 32 64 16 64C7.2 64 0 71.2 0 80s7.2 16 16 16l18.3 0L59.8 452.6C62.1 486.1 90 512 123.6 512l200.8 0c33.6 0 61.4-25.9 63.8-59.4L413.7 96 432 96c8.8 0 16-7.2 16-16s-7.2-16-16-16l-16 0-32.1 0-47.1 0zm44.8 32L356.3 450.3C355.1 467 341.2 480 324.4 480l-200.8 0c-16.8 0-30.7-13-31.9-29.7L66.4 96l315.3 0z"></path></svg>';
                        trashIcon.style.display = 'inline-flex';
                        trashIcon.style.alignItems = 'center';
                        trashIcon.style.color = isPositive ? '#1B5E20' : '#c62828';
                        
                        combinedBadge.addEventListener('mouseenter', () => {
                            trashIcon.querySelector('svg').style.opacity = '1';
                            combinedBadge.style.transform = 'translateY(-1px)';
                            combinedBadge.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            combinedBadge.style.opacity = '0.9';
                        });
                        combinedBadge.addEventListener('mouseleave', () => {
                            trashIcon.querySelector('svg').style.opacity = '0';
                            combinedBadge.style.transform = 'translateY(0)';
                            combinedBadge.style.boxShadow = 'none';
                            combinedBadge.style.opacity = '1';
                        });
                        
                        trashIcon.addEventListener('click', (e) => {
                            e.stopPropagation();
                            editor.chain().focus().command(({ tr }) => {
                                const pos = getPos();
                                if (pos !== null && pos !== undefined) {
                                    tr.delete(pos, pos + newNode.nodeSize);
                                    return true;
                                }
                                return false;
                            }).run();
                        });

                        combinedBadge.appendChild(trashIcon);
                        span.appendChild(combinedBadge);
                    }
                    return true; // Indicate that the update was handled successfully
                }
            };
        };
    }
});

const Total = Node.create({
    name: 'total',
    group: 'inline',
    priority: 1000,
    inline: true,
    atom: true,
    selectable: false,

    addAttributes() {
        return {
            id: { default: '' },
            label: { default: 'Total' },
            value: { default: '' },
            'data-total-fields': { default: '[]' },
            'editor-id': { default: '' },
            'data-margin': { default: '' },
            'data-profit': { default: '' },
            'data-cost': { default: '' },
            'data-suppliers': { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-total]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-total': 'true',
                'data-total-fields': HTMLAttributes['data-total-fields'],
                'data-margin': HTMLAttributes['data-margin'],
                'data-profit': HTMLAttributes['data-profit'],
                'data-cost': HTMLAttributes['data-cost'],
                'data-suppliers': HTMLAttributes['data-suppliers'],
            }),
            HTMLAttributes.value || HTMLAttributes.label,
        ];
    },

    addNodeView() {
        return ({ node, getPos, editor }) => {
            const NumberFormat = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
            const marginStr = 'Margin: ' + NumberFormat.format(node.attrs['data-margin']);

            const outer = document.createElement('span');
            const span = document.createElement('span');
            const valueText = node.attrs.value || node.attrs.label;
            // Currency value
            const valueSpan = document.createElement('span');
            valueSpan.textContent = valueText;
            valueSpan.style.fontWeight = '500';
            valueSpan.style.color = '#334155';
            valueSpan.style.fontSize = '12px';
            span.appendChild(valueSpan);
            span.style.cursor = 'pointer';
            span.style.border = '1px solid #FBC02D';
            span.style.borderRadius = '6px';
            span.style.padding = '4px 12px';
            span.style.backgroundColor = '#FFF9C4';
            span.style.fontFamily = 'inherit';
            span.style.display = 'inline-flex';
            span.style.flexWrap = 'wrap';
            span.style.alignItems = 'center';
            span.style.gap = '8px';
            span.style.lineHeight = '1.5';
            const editorId = node.attrs['editor-id'];

            outer.appendChild(span);
            outer.appendChild(document.createTextNode(' '));

            if ((node.attrs['data-margin'] || node.attrs['data-margin'] === 0) || node.attrs['data-cost']) {
                const combinedBadge = document.createElement('span');
                const isPositive = (node.attrs['data-margin'] || 0) >= 0;
                
                // Build the badge text with dot dividers
                const parts = [];
                
                // Add margin if available
                if (node.attrs['data-margin'] || node.attrs['data-margin'] === 0) {
                    parts.push(marginStr + '%');
                }
                
                // Add profit/loss if available
                if (node.attrs['data-profit']) {
                    const profitStr = (isPositive ? 'Profit: ' : 'Loss: ') + node.attrs['data-profit'];
                    parts.push(profitStr);
                }
                
                // Add costs and suppliers if available
                if (node.attrs['data-cost']) {
                    let costText = 'Costs: ' + node.attrs['data-cost'];
                    if (node.attrs['data-suppliers']) {
                        costText += ' to ' + node.attrs['data-suppliers'];
                    }
                    parts.push(costText);
                }
                
                combinedBadge.textContent = parts.join(' · ');
                combinedBadge.style.color = isPositive ? '#1B5E20' : '#c62828';
                combinedBadge.style.backgroundColor = isPositive ? '#C8E6C9' : '#ffebee';
                combinedBadge.style.border = isPositive ? '1px solid #81C784' : '1px solid #ef5350';
                combinedBadge.style.padding = '2px 10px';
                combinedBadge.style.borderRadius = '12px';
                combinedBadge.style.fontSize = '11px';
                combinedBadge.style.fontWeight = '400';
                combinedBadge.style.display = 'inline-flex';
                combinedBadge.style.alignItems = 'center';
                combinedBadge.style.whiteSpace = 'nowrap';
                combinedBadge.style.position = 'relative';
                combinedBadge.style.cursor = 'pointer';
                combinedBadge.style.transition = 'all 0.2s ease-in-out';
                
                // Add trash icon that appears on hover
                const trashIcon = document.createElement('span');
                trashIcon.innerHTML = '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash" style="width: 10px; height: 10px; margin-left: 6px; opacity: 0; transition: opacity 0.2s; cursor: pointer;" class="svg-inline--fa fa-trash" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M177.7 32l92.5 0c5.5 0 10.6 2.8 13.6 7.5L299.1 64 148.9 64l15.3-24.5c2.9-4.7 8.1-7.5 13.6-7.5zM336.9 64L311 22.6C302.2 8.5 286.8 0 270.3 0L177.7 0C161.2 0 145.8 8.5 137 22.6L111.1 64 64.1 64 32 64 16 64C7.2 64 0 71.2 0 80s7.2 16 16 16l18.3 0L59.8 452.6C62.1 486.1 90 512 123.6 512l200.8 0c33.6 0 61.4-25.9 63.8-59.4L413.7 96 432 96c8.8 0 16-7.2 16-16s-7.2-16-16-16l-16 0-32.1 0-47.1 0zm44.8 32L356.3 450.3C355.1 467 341.2 480 324.4 480l-200.8 0c-16.8 0-30.7-13-31.9-29.7L66.4 96l315.3 0z"></path></svg>';
                trashIcon.style.display = 'inline-flex';
                trashIcon.style.alignItems = 'center';
                trashIcon.style.color = isPositive ? '#1B5E20' : '#c62828';
                
                // Show trash icon on hover and add clickable effect
                combinedBadge.addEventListener('mouseenter', () => {
                    trashIcon.querySelector('svg').style.opacity = '1';
                    combinedBadge.style.transform = 'translateY(-1px)';
                    combinedBadge.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    combinedBadge.style.opacity = '0.9';
                });
                combinedBadge.addEventListener('mouseleave', () => {
                    trashIcon.querySelector('svg').style.opacity = '0';
                    combinedBadge.style.transform = 'translateY(0)';
                    combinedBadge.style.boxShadow = 'none';
                    combinedBadge.style.opacity = '1';
                });
                
                // Delete field on trash icon click
                trashIcon.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const fieldId = node.attrs.id;
                    editor.chain().focus().command(({ tr, state }) => {
                        const pos = getPos();
                        if (pos !== null && pos !== undefined) {
                            tr.delete(pos, pos + node.nodeSize);
                            // Trigger field deletion event after deletion
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent(`fieldDeleted-${editorId}`, {
                                    detail: { id: fieldId }
                                }));
                            }, 0);
                            return true;
                        }
                        return false;
                    }).run();
                });
                
                combinedBadge.appendChild(trashIcon);
                span.appendChild(combinedBadge);
            }

            outer.onclick = () => {
                const event = new CustomEvent(`totalClicked-${editorId}`, {
                    detail: {
                        id: node.attrs.id,
                        checkedFields: JSON.parse(node.attrs['data-total-fields'] || '[]'),
                        update: (value, fields, margin, profitStr, costStr, suppliersStr) => {
                            if (_.isNull(value) && _.isNull(fields)) return;
                            editor.chain().focus().command(({ tr }) => {
                                const pos = getPos();
                                if (pos === undefined || pos === null) {
                                    return false;
                                }
                                tr.setNodeMarkup(pos, undefined, {
                                    ...node.attrs,
                                    'data-total-fields': JSON.stringify(fields),
                                    'data-margin': margin,
                                    'data-profit': profitStr,
                                    'data-cost': costStr,
                                    'data-suppliers': suppliersStr,
                                    value,
                                });
                                return true;
                            }).run();
                        },
                    },
                });

                window.dispatchEvent(new CustomEvent(`totalClicked-${editorId}`, event));
            };

            return { 
                dom: outer,
            };
        };
    }
});

// 3. Toolbar Component
const Toolbar = (props) => {
    const { editor, onFieldCreated, onTotalCreated, fieldLabel = 'Selling Rate', 
        totalLabel = 'Total', uuid, style, onAddTemplate, onAddTerms, onSpecialPaste, 
        onAddAcceptanceLink, onBlur, customToolbar, organization } = props;

    if (!editor) return null;

    const buttonStyle = {
        padding: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontSize: '18px',
        color: '#5f6368',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
    };

    const activeButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#e8f0fe',
        color: '#1a73e8',
    };

    const hoverStyle = {
        backgroundColor: '#f1f3f4',
    };

    const createButton = (onClick, isActive, title, children, disabled = false) => (
        <Button
            sx={{ minWidth: '0px', width: 'auto', padding: '6px', color: '#5f6368 !important' }}
            onClick={onClick}
            disabled={disabled}
        >
            <Tooltip title={title}>
                {children}
            </Tooltip>
        </Button>
    );

    return (
        <div style={{
            border: '1px solid #dadce0',
            padding: '8px 16px',
            backgroundColor: '#fff',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            minHeight: '56px',
            width: '100%',
            ...style
        }}>
            <Select
                size="small"
                value={editor.getAttributes('textStyle').fontFamily || 'Poppins'}
                onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
                sx={{
                    minWidth: 120,
                    marginRight: '8px',
                    '& .MuiSelect-select': {
                        padding: '4px 8px',
                        fontFamily: 'Poppins, sans-serif',
                    }
                }}
                MenuProps={{
                    style: {
                        zIndex: 100000000000000,
                    },
                }}
                renderValue={(value) => {
                    const font = value || 'Poppins';
                    return <span style={{ fontFamily: font, fontWeight: 500 }}>{font}</span>;
                }}
            >
                {[
                    'Poppins',
                    'Arial',
                    'Helvetica',
                    'Verdana', 
                    'Tahoma',
                    'Trebuchet MS',
                    'Georgia',
                    'Garamond',
                    'Palatino',
                    'Bookman',
                    'Comic Sans MS'
                ].map(font => (
                    <MenuItem key={font} value={font}>
                        <span style={{fontFamily: font}}>{font}</span>
                    </MenuItem>
                ))}
                <MenuItem value="Times New Roman">
                    <span style={{fontFamily: 'Times New Roman'}}>Times New Roman</span>
                </MenuItem>
                <MenuItem value="Courier New">
                    <span style={{fontFamily: 'Courier New'}}>Courier New</span>
                </MenuItem>
            </Select>
            <Select
                size="small"
                value={editor.getAttributes('textStyle').fontSize || '12px'}
                onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
                sx={{
                    minWidth: 80,
                    marginRight: '8px',
                    '& .MuiSelect-select': {
                        padding: '4px 8px',
                    }
                }}
                MenuProps={{
                    style: {
                        zIndex: 1000000000000,
                    },
                }}
            >
                {[
                    '10px',
                    '11px', 
                    '12px',
                    '13px',
                    '14px',
                    '16px',
                    '18px',
                    '20px',
                    '24px',
                    '28px',
                    '32px',
                    '36px',
                    '48px'
                ].map(size => (
                    <MenuItem key={size} value={size}>
                        <span style={{fontSize: '13px', fontFamily: 'Arial'}}>{size}</span>
                    </MenuItem>
                ))}
            </Select>

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />
            {/* Text Formatting */}
            {createButton(
                () => editor.chain().focus().toggleBold().run(),
                editor.isActive('bold'),
                'Bold (Ctrl+B)',
                <FontAwesomeIcon icon={faBold} />
            )}
            {createButton(
                () => editor.chain().focus().toggleItalic().run(),
                editor.isActive('italic'),
                'Italic (Ctrl+I)',
                <FontAwesomeIcon icon={faItalic} />
            )}
            {createButton(
                () => editor.chain().focus().toggleStrike().run(),
                editor.isActive('strike'),
                'Strikethrough',
                <FontAwesomeIcon icon={faStrikethrough} />
            )}
            {createButton(
                () => editor.chain().focus().toggleCode().run(),
                editor.isActive('code'),
                'Monospace',
                <FontAwesomeIcon icon={faCode} />
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Lists */}
            {createButton(
                () => editor.chain().focus().toggleBulletList().run(),
                editor.isActive('bulletList'),
                'Bullet list',
                <FontAwesomeIcon icon={faList} />
            )}
            {createButton(
                () => editor.chain().focus().toggleOrderedList().run(),
                editor.isActive('orderedList'),
                'Numbered list',
                <FontAwesomeIcon icon={faListOl} />
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Headings */}
            {createButton(
                () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
                editor.isActive('heading', { level: 1 }),
                'Heading 1',
                <FontAwesomeIcon icon={faHeading} />
            )}
            {createButton(
                () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
                editor.isActive('heading', { level: 2 }),
                'Heading 2',
                <FontAwesomeIcon icon={faHeadingLight} />
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Blockquote */}
            {createButton(
                () => editor.chain().focus().toggleBlockquote().run(),
                editor.isActive('blockquote'),
                'Quote',
                <FontAwesomeIcon icon={faQuoteLeft} />
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Horizontal Rule */}
            {createButton(
                () => editor.chain().focus().setHorizontalRule().run(),
                false,
                'Horizontal rule',
                <FontAwesomeIcon icon={faHorizontalRule} />
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {/* Undo/Redo */}
            {createButton(
                () => editor.chain().focus().undo().run(),
                false,
                'Undo (Ctrl+Z)',
                <FontAwesomeIcon icon={faUndo} />,
                !editor.can().undo()
            )}
            {createButton(
                () => editor.chain().focus().redo().run(),
                false,
                'Redo (Ctrl+Y)',
                <FontAwesomeIcon icon={faRedo} />,
                !editor.can().redo()
            )}

            <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />

            {createButton(
                () => {
                    const editorEl = editor.view.dom;
                    editorEl.focus();
                    document.execCommand('cut');
                },
                false,
                'Cut',
                <FontAwesomeIcon icon={faScissors} />,
                false
            )}
            {createButton(
                () => {
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '\n');
                    navigator.clipboard.writeText(text);
                },
                false,
                'Copy',
                <FontAwesomeIcon icon={faCopy} />,
                false
            )}
            {createButton(
                async () => {
                    const text = await readClipboardText();
                    if (text == null) return;
                    editor.commands.insertContent(text);
                },
                false,
                'Paste',
                <FontAwesomeIcon icon={faPaste} />,
                false
            )}
            {onFieldCreated && !customToolbar && createButton(
                () => onSpecialPaste(),
                false,
                'Paste special',
                <FontAwesomeIcon icon={faPasteSolid} />,
                false
            )}

            {(onFieldCreated || onTotalCreated || customToolbar) && <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '2px' }}>
                {/* Field */}
                {onFieldCreated && !customToolbar && createButton(
                    () => {
                        const id = uuidv4();
                        const { from, to } = editor.state.selection;
                        const selectedText = editor.state.doc.textBetween(from, to, '\n');
                        const selectedTextTransformed = (selectedText || '').replace(/[^a-zA-Z0-9]+$/, '').trim();

                        const label = selectedTextTransformed || (_.isFunction(fieldLabel) ? fieldLabel(id) : fieldLabel);
                        
                        editor.chain()
                            .focus()
                            .insertContent(selectedText+ ' ')
                            .insertContent({
                                type: 'field',
                                attrs: {
                                    id: id,
                                    label,
                                    'editor-id': uuid,
                                    value: ''
                                }
                            })
                            .run();

                        onFieldCreated({ id, title: selectedTextTransformed, update: (value, margin, profit, cost, suppliers, title, section) => {
                            const allNodes = findChildren(editor.state.doc, node => node.type.name === 'field' && node.attrs.id === id);
                            if (allNodes.length > 0) {
                                const { node, pos } = allNodes[0];
                                editor.view.dispatch(
                                    editor.state.tr.setNodeMarkup(pos, node.type, {
                                        ...node.attrs,
                                        value,
                                        'data-margin': margin || '',
                                        'data-profit': profit || '',
                                        'data-cost': cost || '',
                                        'data-suppliers': suppliers || '',
                                        'data-title': title || '',
                                        'data-section': section || '',
                                    })
                                )
                            }
                            onBlur({ html: editor.getHTML() });
                        } });
                    },
                    false,
                    'Add Charge',
                    <FontAwesomeIcon style={{ color: '#605DE4', fontSize: '16px' }} icon={faSquareDollar} />
                )}

                {(onAddTemplate || onAddTerms) && !customToolbar && <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />}

                {/* Total */}
                {onTotalCreated && !customToolbar && createButton(
                    async () => {
                        const id = uuidv4();
                        try {
                            const selection = editor.state.selection;
                            const selectedFieldIds = [];
                            
                            if (!selection.empty) {
                                // Find all field nodes within selection
                                const from = selection.from;
                                const to = selection.to;
                                
                                editor.state.doc.nodesBetween(from, to, (node) => {
                                    if (node.type.name === 'field') {
                                        selectedFieldIds.push(node.attrs.id);
                                    }
                                });
                            }

                            const { sum, checkedFields, margin, profitStr, costStr, suppliersStr, title, section } = await onTotalCreated({ 
                                id,
                                selectedFieldIds: selectedFieldIds.length ? selectedFieldIds : undefined 
                            });

                            editor.chain().focus().insertContent({
                                type: 'total',
                                attrs: {
                                    id: id,
                                    label: _.isFunction(totalLabel) ? totalLabel(id) : totalLabel,
                                    'editor-id': uuid,
                                    'data-total-fields': JSON.stringify(checkedFields || []),
                                    value: sum || '',
                                    'data-margin': margin || '',
                                    'data-profit': profitStr || '',
                                    'data-cost': costStr || '',
                                    'data-suppliers': suppliersStr || ''
                                }
                            }).run();
                        } catch (e) {}
                    },
                    false,
                    'Add Total',
                    <FontAwesomeIcon style={{ color: '#605DE4', fontSize: '16px' }} icon={faSquarePlus} />
                )}

                {onAddTemplate && !customToolbar && (
                    <>
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />
                        {createButton(
                            () => {
                                onAddTemplate({
                                    callback: (preface) => {
                                        const _preface = (preface || '').replace(/editor-id="[0-9a-f-]{36}"/g, `editor-id="${uuid}"`);
                                        editor.commands.insertContent(_preface)
                                    }
                                })
                            },
                            false,
                            'Add Template',
                            <FontAwesomeIcon style={{ color: '#605DE4', fontSize: '16px' }} icon={faWpforms} />
                        )}
                    </>
                )}

                {onAddTerms && !customToolbar && (
                    <>
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />
                        {createButton(
                            onAddTerms,
                            false,
                            'Add Terms & Conditions',
                            <FontAwesomeIcon style={{ color: '#605DE4', fontSize: '16px' }} icon={faFileCheck} />
                        )}
                    </>
                )}

                {!customToolbar && (
                    <>
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#dadce0', margin: '0 8px' }} />
                        {createButton(
                            () => {
                                const linkText = getAcceptQuoteLinkText({ organization, link: 'https://PLACEHOLDER_FOR_LINK' });
                                editor.commands.insertContent(`<p>${linkText}</p>`);
                            },
                            false,
                            'Add Acceptance Link',
                            <FontAwesomeIcon style={{ color: '#605DE4', fontSize: '16px' }} icon={faLink} />
                        )}
                    </>
                )}

                {customToolbar}
            </div>
        </div>
    );
};


export const FontSize = Extension.create({
    name: 'fontSize',

    addOptions() {
        return {
            types: ['textStyle'],
        }
    },

    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize || null,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {}
                            }
                            return { style: `font-size: ${attributes.fontSize}` }
                        },
                    },
                },
            },
        ]
    },

    addCommands() {
        return {
            setFontSize:
                size =>
                    ({ chain }) => {
                        return chain()
                            .setMark('textStyle', { fontSize: size })
                            .run()
                    },
            unsetFontSize:
                () =>
                    ({ chain }) => {
                        return chain()
                            .setMark('textStyle', { fontSize: null })
                            .removeEmptyTextStyle()
                            .run()
                    },
        }
    },
})

function OpportunityEditor(props, ref) {
    const { onFieldClicked, onTotalClicked, onTotalNeedsUpdate, onFieldCreated, onTotalCreated,
        onAddTemplate, onAddTerms, onBlur, onFieldDeleted, onFieldDeleteRequested, initialContent, onFieldsCreatedFromRate, fieldLabel,
        totalLabel, editable = true, toolbarStyle, sx, transportationMethod, organization, customToolbar,
        onUploadImage, noBottomBorder = false } = props;

    const [ dialogs, setDialogs ] = useState([]);

    const [ uuid ] = useState(uuidv4());

    let editor = null;
    const editableRef = useRef(editable);
    editableRef.current = editable;

    const FieldDeleteDetector = Extension.create({
        name: 'fieldDeleteDetector',
        addProseMirrorPlugins() {
            return [
                new Plugin({
                    key: new PluginKey('fieldDeleteDetector'),
                    view: () => ({
                        update: (view, prevState) => {
                            if (!editableRef.current) return;
                            if (prevState.doc.eq(view.state.doc)) return;

                            const oldFields = new Map();
                            prevState.doc.descendants(node => {
                                if (node.type.name === 'field') {
                                    oldFields.set(node.attrs.id, {
                                        label: node.attrs['data-title'] || node.attrs.label,
                                        value: node.attrs.value || node.attrs.label,
                                    });
                                }
                            });
                            const newFieldIds = new Set();
                            view.state.doc.descendants(node => {
                                if (node.type.name === 'field') newFieldIds.add(node.attrs.id);
                            });

                            const deletedFields = [...oldFields.entries()]
                                .filter(([id]) => !newFieldIds.has(id))
                                .map(([id, info]) => ({ id, ...info }));
                            if (deletedFields.length === 0) return;

                            window.dispatchEvent(new CustomEvent(`fieldDeleteRequested-${uuid}`, {
                                detail: {
                                    ids: deletedFields.map(f => f.id),
                                    fields: deletedFields,
                                }
                            }));
                        }
                    }),
                }),
            ];
        },
    });

    const editorExtensions = [
        StarterKit.configure({
            paragraph: false, // Disable default paragraph
        }),
        DivParagraph,
        Field,
        Total,
        TextStyle,
        FontFamily,
        FontSize,
        FieldDeleteDetector,
        // Without table nodes in the schema a pasted spreadsheet is flattened into loose
        // paragraphs - the editor keeps only what it can model. Registering them lets a
        // table copied from Excel paste in as a table, the way it already does in the
        // email composer (which is a plain contentEditable and so never filtered it).
        ...richPasteExtensions,
        // Link is included in StarterKit as a Mark
    ];

    const onFieldClickedRef = useRef(onFieldClicked);
    onFieldClickedRef.current = onFieldClicked;

    const onTotalClickedRef = useRef(onTotalClicked);
    onTotalClickedRef.current = onTotalClicked;

    const onTotalNeedsUpdateRef = useRef(onTotalNeedsUpdate);
    onTotalNeedsUpdateRef.current = onTotalNeedsUpdate;

    const onFieldDeletedRef = useRef(onFieldDeleted);
    onFieldDeletedRef.current = onFieldDeleted;

    const onFieldDeleteRequestedRef = useRef(onFieldDeleteRequested);
    onFieldDeleteRequestedRef.current = onFieldDeleteRequested;

    const onFieldsCreatedFromRateRef = useRef(onFieldsCreatedFromRate);
    onFieldsCreatedFromRateRef.current = onFieldsCreatedFromRate;

    editor = useEditor({
        extensions: editorExtensions,
        content: (initialContent || '').replace(/editor-id="[0-9a-f-]{36}"/g, `editor-id="${uuid}"`).replace(/<div><\/div>/g, '&nbsp;'),
        editorProps: {
            attributes: {
                style: "font-family: 'Poppins', sans-serif;",
            },
            // A spreadsheet's formatting lives in a <style> block it ships alongside the
            // table. Fold it onto the cells while the stylesheet is still here to read.
            transformPastedHTML: (html) => resolveStyleBlocks(html),
            handlePaste: createImagePasteHandler({
                getEditor: () => editor,
                onUploadImage,
                onChanged: ({ html }) => onBlur({ html }),
            }),
        },
    });
    editor.setEditable(editable);

    // Function to update field value
    const updateFieldValue = (fieldId, value, margin, profit, cost, suppliers, title, section) => {
        if (!editor) return;

        editor.chain().command(({ tr, state }) => {
            const allNodes = findChildren(state.doc, node =>
                node.type.name === 'field' && node.attrs.id === fieldId
            );
            if (allNodes.length > 0) {
                const { node, pos } = allNodes[0];
                tr.setNodeMarkup(pos, node.type, {
                    ...node.attrs,
                    value: value,
                    'data-margin': margin || '',
                    'data-profit': profit || '',
                    'data-cost': cost || '',
                    'data-suppliers': suppliers || '',
                    'data-title': title || '',
                    'data-section': section || '',
                });
                return true;
            }
            return false;
        }).run();
    };

    function requestUpdateTotals() {
        const allNodes = findChildren(editor.state.doc, () => true);
        const totals = allNodes.filter(t => t.node.type.name === 'total');

        totals.forEach(({ node, pos }) => {
            onTotalNeedsUpdateRef.current({
                fields: JSON.parse(node.attrs['data-total-fields']),
                callback: (value, fields, margin, profitStr, costStr, suppliersStr ) => {
                    editor.view.dispatch(
                        editor.state.tr.setNodeMarkup(pos, node.type, {
                            ...node.attrs,
                            'data-total-fields': JSON.stringify(fields),
                            value,
                            'data-margin': margin || '',
                            'data-profit': profitStr || '',
                            'data-cost': costStr || '',
                            'data-suppliers': suppliersStr || '',
                        })
                    )
                }
            });
        })
        onBlur({ html: editor.getHTML() });
    }

    const getFieldIds = () => {
        if (!editor) return [];
        return findChildren(editor.state.doc, node => node.type.name === 'field').map(n => n.node.attrs.id);
    };

    const getFieldValues = () => {
        if (!editor) return {};
        const fields = findChildren(editor.state.doc, node => node.type.name === 'field');
        return _.fromPairs(fields.map(n => [n.node.attrs.id, n.node.attrs.value]));
    };

    const markFieldStale = (fieldId, stale) => {
        if (!editor) return;
        editor.chain().command(({ tr, state }) => {
            const allNodes = findChildren(state.doc, node =>
                node.type.name === 'field' && node.attrs.id === fieldId
            );
            if (allNodes.length > 0) {
                const { node, pos } = allNodes[0];
                tr.setNodeMarkup(pos, node.type, {
                    ...node.attrs,
                    'data-stale': stale ? 'true' : '',
                });
                return true;
            }
            return false;
        }).run();
    };

    const getTotalFieldIds = () => {
        if (!editor) return [];
        const totals = findChildren(editor.state.doc, node => node.type.name === 'total');
        const ids = new Set();
        totals.forEach(({ node }) => {
            JSON.parse(node.attrs['data-total-fields'] || '[]').forEach(id => ids.add(id));
        });
        return [...ids];
    };

    // Expose the update function via ref
    React.useImperativeHandle(ref, () => ({
        updateFieldValue,
        requestUpdateTotals,
        getFieldIds,
        getFieldValues,
        getTotalFieldIds,
        markFieldStale,
    }));

    useEffect(() => {
        const handler = (e) => {
            onFieldClickedRef.current(e.detail);
        };
        const handlerUpdated = (e) => {
            const allNodes = findChildren(editor.state.doc, () => true);
            const totals = allNodes.filter(t => t.node.type.name === 'total');

            totals.forEach(({ node, pos }) => {
                onTotalNeedsUpdateRef.current({
                    fields: JSON.parse(node.attrs['data-total-fields']),
                    callback: (value, fields, margin, profitStr, costStr, suppliersStr ) => {
                        editor.view.dispatch(
                            editor.state.tr.setNodeMarkup(pos, node.type, {
                                ...node.attrs,
                                'data-total-fields': JSON.stringify(fields),
                                value,
                                'data-margin': margin || '',
                                'data-profit': profitStr || '',
                                'data-cost': costStr || '',
                                'data-suppliers': suppliersStr || '',
                            })
                        )
                        onBlur({ html: editor.getHTML() });
                    }
                });
            })

            onBlur({ html: editor.getHTML() });
        };
        const handlerDeleted = (e) => {
            if (onFieldDeletedRef.current) {
                onFieldDeletedRef.current(e.detail);
            }
        };
        const handlerDeleteRequested = (e) => {
            if (onFieldDeleteRequestedRef.current) {
                onFieldDeleteRequestedRef.current(e.detail);
            }
        };
        window.addEventListener(`fieldClicked-${uuid}`, handler);
        window.addEventListener(`fieldUpdated-${uuid}`, handlerUpdated);
        window.addEventListener(`fieldDeleted-${uuid}`, handlerDeleted);
        window.addEventListener(`fieldDeleteRequested-${uuid}`, handlerDeleteRequested);
        return () => {
            window.removeEventListener(`fieldClicked-${uuid}`, handler);
            window.removeEventListener(`fieldUpdated-${uuid}`, handlerUpdated);
            window.removeEventListener(`fieldDeleted-${uuid}`, handlerDeleted);
            window.removeEventListener(`fieldDeleteRequested-${uuid}`, handlerDeleteRequested);
        };
    }, []);

    useEffect(() => {
        const handler = (e) => {
            onTotalClickedRef.current(e.detail);
        };
        window.addEventListener(`totalClicked-${uuid}`, handler);
        return () => window.removeEventListener(`totalClicked-${uuid}`, handler);
    }, []);

    const [{ isOver }, dropRef] = useDrop(() => ({
        accept: ['RateCardSingleRate', 'RateCardSingleSection', 'RateCard'], 
        drop: (item, monitor) => {
            if (!editor) return {}
            if (!editableRef.current) return {}

            const client = monitor.getClientOffset();
            const { view, state } = editor;
            const { schema } = state;
            if (!client) return {}

            // 1) screen coords -> editor doc position
            const posAt = view.posAtCoords({ left: client.x, top: client.y })?.pos
                ?? state.selection.from

            let fragment = null;

            const conversions = {};
            const nodes = {};

            const fields = item.section ? item.section.offers[0].fields.map(f => ({ ...f, section: item.section.title })) : 
                item.field ? [item.field] :
                    item.rate.offer.sections.map(s => s.offers[0].fields.map(f => ({ ...f, section: s.title }))).flat();

            const stringFields = fields?.filter(f => f.type === 'string');
            const nonStringFields = fields?.filter(f => f.type !== 'string');

            if (nonStringFields) {
                const _nodes = nonStringFields.map(f => {
                    const newId = uuidv4();
                    conversions[f.id] = newId;
                    nodes[newId] = schema.nodes.field.create({
                        id: newId,
                        label: f.title,
                        value: '0',
                        'editor-id': uuid
                    })
                    return schema.nodes.paragraph.create(
                        null,
                        Fragment.fromArray([
                            schema.text(`${f.section ? `${f.section} / ` : ''}${f.title}: `),
                            nodes[newId]
                        ]))
                });
                
                stringFields.forEach(f => {
                    if (!f.values?.string?.value) return;
                    _nodes.push(schema.nodes.paragraph.create(
                        null,
                        Fragment.fromArray([
                            schema.text(`${f.title}: `),
                            schema.text(f.values?.string?.value)
                        ]))
                    )
                });

                fragment = Fragment.fromArray(_nodes)
            }

            // 3) Find a valid insertion point near the drop
            const slice = new Slice(fragment, 0, 0);
            const insertPos = dropPoint(state.doc, posAt, slice) ?? posAt;

            // (optional) Ensure parent can accept this node
            const $pos = state.doc.resolve(insertPos)
            /*
            if (!$pos.parent.canReplaceWith($pos.index(), $pos.index(), fragment)) {
                return {} // can't insert here
            }
                */

            // 4) Replace at that position with your node
            const tr = state.tr.replaceWith(insertPos, insertPos, fragment);
            view.dispatch(tr.scrollIntoView());
            onFieldsCreatedFromRateRef.current({
                rate: item.rate,
                fields: nonStringFields.map(f => _.omit(f, 'section')),
                idConversions: conversions,
                callbackValues: ({ fields, addMetaData }) => {
                    fields.forEach(({ id, value, margin, profit, cost, suppliers, title, section }) => {
                        const node = nodes[id];
                        const matches = findChildren(editor.state.doc, n => n === node);
                        if (!matches.length) return;
                        const pos = matches[0].pos;
                        editor.view.dispatch(
                            editor.state.tr.setNodeMarkup(pos, node.type, {
                                ...node.attrs,
                                value,
                                'data-margin': margin || '',
                                'data-profit': profit || '',
                                'data-cost': cost || '',
                                'data-suppliers': suppliers || '',
                                'data-title': title || '',
                                'data-section': section || '',
                            })
                        )
                    });
                    if (addMetaData && item.rate) {
                        // Add text prior to the editor saying "Carrier: [carrier name]" (item.rate.supplier.organization)
                        if (item.rate.supplier?.organization) {
                            const text = `Carrier: ${item.rate.supplier.organization}`;
                            editor.commands.insertContent(text);
                            editor.commands.insertContent('\n');
                        }

                        // Add transit time
                        const transitTime = item.rate.offer?.transitTime;
                        if (transitTime) {
                            let transitTimeText;
                            if (item.rate.transportationMethod === 'sea') {
                                transitTimeText = `${transitTime} ${pluralize('day', transitTime)}`;
                            } else {
                                const hours = Math.floor(transitTime);
                                const minutes = Math.floor((transitTime - hours) * 60);
                                const parts = [];
                                if (hours > 0) parts.push(`${hours} ${pluralize('hour', hours)}`);
                                if (minutes > 0) parts.push(`${minutes} ${pluralize('min', minutes)}`);
                                transitTimeText = parts.join(' and ') || `${transitTime} ${pluralize('hour', transitTime)}`;
                            }
                            editor.commands.insertContent(`Transit time: ${transitTimeText}`);
                            editor.commands.insertContent('\n');
                        }

                        // Add text about route
                        if (item.rate.source?.text && item.rate.destination?.text) {
                            const route = `${item.rate.source.text} to ${item.rate.destination.text}`;
                            editor.commands.insertContent(`Route: ${route}`);
                            editor.commands.insertContent('\n');
                        }

                        // Add validity date
                        const validUntil = item.rate.offer?.validUntil;
                        if (validUntil && dayjs(validUntil).isValid()) {
                            const validUntilText = `Valid until: ${dayjs(validUntil).format('MMM D, YYYY')}`;
                            editor.commands.insertContent(validUntilText);
                            editor.commands.insertContent('\n');
                        }
                    }

                    onBlur({ html: editor.getHTML() });
                }
            });
            return { inserted: true };
        },
        collect: (m) => ({ isOver: m.isOver({ shallow: true }) }),
    }))

    // A pasted screenshot is raw image data, not markup. Show it immediately as base64 so
    // the paste feels instant, then swap in the hosted URL once it has uploaded - a quote
    // carrying a full screenshot inline would bloat the opportunity it is stored in, and
    // base64 images are unreliable in mail clients anyway. Without an uploader the base64
    // simply stays, which is still better than the paste doing nothing.

    async function onSpecialPaste() {

        function callback({ modified }) {
            modified = modified.replace(/editor-id=""/g, `editor-id="${uuid}"`);
            editor.commands.insertContent(modified)
        }

        // Get clipboard content into text
        const text = await readClipboardText();
        if (text == null) return;

        // Handle special paste from excel or from a simple list of [field name] - [price]
        const lines = _.compact(text.split('\n'));
        if (lines.length <= 1) return callback({ modified: text })

        // Check if lines contain consistent delimiters (tab or -)
        const hasConsistentTabs = lines.every(line => line.includes('\t'));
        const hasConsistentDashes = lines.every(line => line.includes(' - ') || line.includes(' – ') || line.includes(': '));

        if (!hasConsistentTabs && !hasConsistentDashes) return callback({ modified: text })

        const delimiter = hasConsistentTabs ? '\t' : /\s*[-–:]\s*/;
        const columns = lines.map(line => line.split(delimiter).map(col => col.trim()));

        setDialogs(d => [...d, {
            id: uuidv4(), open: 'excel-paste', excelData: columns, callback: ({ result }) => {
                if (!result) return;
                result.forEach(r => r.id = uuidv4());
                const html = result.map(r => {
                    const name = r.name;
                    const price = currencyToText(r.currency, true, r.price.toLocaleString({ maximumFractionDigits: 2 }));
                    return `<p>${name} - <span id="${r.id}" label="${name}" editor-id="" value="${price}" data-field="true" data-margin="100" data-profit="${price}" data-cost="" data-suppliers="" data-title="" data-section=""><span style="color: blue; text-decoration: underline; cursor: pointer;">${price}</span></span></p>`;
                }).join('\n');
                callback({ modified: html });

                result.forEach(r => {
                    onFieldCreated({ id: r.id, type: r.chargeType, title: r.name, value: r.price, currency: r.currency });
                });
            }
        }]);
    }
    if (!editor) return null;

    return (
        <div onClick={() => editor.chain().focus().run()} style={{ padding: '1rem' , paddingTop: '0px', cursor: !editable ? 'default' : 'text', ...(sx || {}) }} ref={dropRef}>
            {editable && (
                <Toolbar 
                    editor={editor} 
                    onFieldCreated={onFieldCreated} 
                    onTotalCreated={onTotalCreated} 
                    fieldLabel={fieldLabel} 
                    totalLabel={totalLabel} 
                    uuid={uuid} 
                    style={toolbarStyle}
                    onAddTemplate={onAddTemplate}
                    onAddTerms={onAddTerms}
                    onSpecialPaste={onSpecialPaste}
                    customToolbar={customToolbar}
                    onBlur={onBlur}
                    organization={organization}
                />
            )}
            <EditorContent 
                editor={editor}
                onBlur={() => {
                    if (onBlur) {
                        let html = editor.getHTML();
                        // Ensure the HTML has Poppins as default font-family
                        // If no font-family is set on the root, wrap it with a div that has Poppins
                        if (!html.includes('font-family') && !html.includes('fontFamily')) {
                            html = `<div style="font-family: 'Poppins', sans-serif;">${html}</div>`;
                        }
                        onBlur({ html });
                    }
                }}
                style={{ 
                    border: '1px solid #dadce0', 
                    borderTop: !editable ? '1px solid #dadce0' : 'none',
                    borderBottom: noBottomBorder ? 'none' : '1px solid #dadce0',
                    padding: '16px', 
                    borderRadius: !editable ? '8px' : noBottomBorder ? '0px' : '0 0 8px 8px',
                    minHeight: '300px',
                    height: '400px',
                    overflowY: 'auto',
                    lineHeight: '1.5',
                    fontSize: '12px',
                    fontFamily: "'Poppins', sans-serif",
                    outline: 'none',
                }} 
            />
            <style>
                {`
                    .ProseMirror {
                        outline: none !important;
                        border: none !important;
                        font-size: 12px;
                        font-family: 'Poppins', sans-serif !important;
                    }
                    .ProseMirror:focus {
                        outline: none !important;
                        border: none !important;
                    }
                    .ProseMirror * {
                        outline: none !important;
                    }
                    .ProseMirror div:not([data-field]):not([data-total]),
                    .ProseMirror p,
                    .ProseMirror li,
                    .ProseMirror pre {
                        font-size: 12px;
                        font-family: 'Poppins', sans-serif;
                    }
                    .ProseMirror ul li div {
                        font-size: 12px;
                    }
                    .ProseMirror ul li {
                        margin-bottom: 8px;
                    }
                    .ProseMirror ul li:last-child {
                        margin-bottom: 0;
                    }
                `}
            </style>
            {dialogs.map(dialog => {
                if (dialog.open === 'excel-paste') {
                    return (
                        <ExcelPasteDialog
                            key={dialog.id} 
                            open={dialog.open === 'excel-paste'}
                            onClose={result => {
                                setDialogs(d => _.initial(d));
                                if (result && dialog.callback) dialog.callback(result);
                            }}
                            excelData={dialog.excelData}
                            transportationMethod={transportationMethod}
                            organization={organization}
                        />
                    )
                }
            })}
        </div>
    );
}

export default React.forwardRef(OpportunityEditor);